import type { ReactNode } from 'react';
import { PROVENANCE_LABELS, type Provenance } from '@ave/shared';

export type Tone = 'slate' | 'teal' | 'emerald' | 'amber' | 'rose' | 'sky' | 'indigo' | 'violet';

const TONE_BADGE: Record<Tone, string> = {
  slate: 'bg-slate-100 text-slate-600 ring-slate-500/20',
  teal: 'bg-teal-50 text-teal-700 ring-teal-600/20',
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  amber: 'bg-amber-50 text-amber-800 ring-amber-600/30',
  rose: 'bg-rose-50 text-rose-700 ring-rose-600/20',
  sky: 'bg-sky-50 text-sky-700 ring-sky-600/20',
  indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
  violet: 'bg-violet-50 text-violet-700 ring-violet-600/20',
};

export function Badge({
  children,
  tone = 'slate',
  title,
}: {
  children: ReactNode;
  tone?: Tone;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${TONE_BADGE[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * Provenance is the product's central discipline, so it gets a consistent
 * visual weight everywhere it appears: the weaker the provenance, the
 * warmer the colour. A reviewer scanning a page should be able to find the
 * asserted facts without reading a word.
 */
const PROVENANCE_TONE: Record<Provenance, Tone> = {
  registry_confirmed: 'emerald',
  external_expert: 'sky',
  corroborated: 'teal',
  data_derived: 'slate',
  management_asserted: 'amber',
};

export function ProvenanceChip({ provenance }: { provenance: Provenance }) {
  return (
    <Badge
      tone={PROVENANCE_TONE[provenance]}
      title={
        provenance === 'management_asserted'
          ? 'Asserted by management and not independently corroborated — the report will say so'
          : provenance === 'corroborated'
            ? 'Asserted by management and independently supported by a data signal'
            : undefined
      }
    >
      {PROVENANCE_LABELS[provenance]}
    </Badge>
  );
}

export function Card({
  children,
  className = '',
  as: As = 'div',
}: {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'section' | 'article';
}) {
  return <As className={`card ${className}`}>{children}</As>;
}

export function SectionHeading({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle?: string;
  right?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-4">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {subtitle ? <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>
  );
}

export function EmptyState({
  title,
  detail,
  action,
}: {
  title: string;
  detail?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <p className="text-sm font-medium text-slate-700">{title}</p>
      {detail ? <p className="mt-1 max-w-md text-sm text-slate-500">{detail}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="label">{label}</dt>
      <dd className="mt-1 text-sm text-slate-800">{children}</dd>
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-sm text-slate-500">
      <svg className="h-4 w-4 animate-spin text-teal-600" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path
          className="opacity-90"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        />
      </svg>
      {label ?? 'Loading'}
    </div>
  );
}

export function ErrorNotice({ title, message }: { title: string; message: string }) {
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
      <p className="text-sm font-semibold text-rose-900">{title}</p>
      <p className="mt-0.5 text-sm text-rose-800">{message}</p>
    </div>
  );
}
